import React, { useState } from 'react';

const BillSplitCalculator = () => {
  const [billSubtotal, setBillSubtotal] = useState('');
  const [tipPercentage, setTipPercentage] = useState('');
  const [numberOfPersons, setNumberOfPersons] = useState('');
  const [totalBill, setTotalBill] = useState('₹00.00');
  const [billPerPerson, setBillPerPerson] = useState('₹00.00');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const calculateBill = () => {
    const subtotal = parseFloat(billSubtotal);
    const tip = parseFloat(tipPercentage);
    const persons = parseInt(numberOfPersons);

    if (isNaN(subtotal) || isNaN(tip) || isNaN(persons) || persons <= 0) {
      setError('Please enter valid values.');
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setError('');
      }, 1500);
      return;
    }

    setLoading(true);
    setError('');

    setTimeout(() => {
      const totalTip = (subtotal * tip) / 100;
      const total = subtotal + totalTip;
      const perPerson = total / persons;

      setTotalBill(`₹${total.toFixed(2)}`);
      setBillPerPerson(`₹${perPerson.toFixed(2)}`);
      setLoading(false);
    }, 1500);
  };

  const clearFields = () => {
    setBillSubtotal('');
    setTipPercentage('');
    setNumberOfPersons('');
    setTotalBill('₹00.00');
    setBillPerPerson('₹00.00');
    setError('');
    setLoading(false);
  };

  return (
    <div className="bg-[#f6f6f6] py-6 sm:py-12 px-4 sm:px-6 lg:px-8 font-sans relative overflow-hidden min-h-screen">
      {/* Dotted Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `radial-gradient(circle, #000 1px, transparent 1px)`,
            backgroundSize: '20px 20px sm:30px sm:30px',
            backgroundPosition: '0 0'
          }}
        />
      </div>
      
      {/* Vertical Lines */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(10)].map((_, i) => (
          <div
            key={`v-${i}`}
            className="absolute h-full border-l border-dotted border-black"
            style={{ left: `${i * 10}%` }}
          />
        ))}
      </div>
      
      {/* Horizontal Lines */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(8)].map((_, i) => (
          <div
            key={`h-${i}`}
            className="absolute w-full border-t border-dotted border-black"
            style={{ top: `${i * 12.5}%` }}
          />
        ))}
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="mb-6 sm:mb-10 text-left">
          <h2 className="text-4xl sm:text-6xl font-black tracking-tight text-[#000000] leading-tight" style={{ fontFamily: 'Arial, sans-serif' }}>
            <span className="block italic">BILL</span>
            <span className="block text-[#ff5722] italic">SPLIT</span>
          </h2>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-700 max-w-xl font-medium">
            Calculate how much each person should pay when splitting a bill, including tips.
          </p>
          <div className="mt-3 sm:mt-4 flex items-center gap-3 sm:gap-4">
            <div className="bg-black text-white px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold">
              Smart Calculator
            </div>
            <div className="text-xl sm:text-2xl">*</div>
          </div>
        </div>

        {/* Main Calculator Card */}
        <div 
          className="rounded-[12px] sm:rounded-[16px] overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 sm:hover:-translate-y-2"
          style={{
            backgroundColor: "#ff5722",
            color: "#000000",
            border: "2px solid #ff7043",
            fontFamily: 'Arial, sans-serif'
          }}
        >
          <div className="p-4 sm:p-6 relative">
            <h3 className="text-xl sm:text-2xl font-black tracking-tight uppercase mb-3 sm:mb-4 text-center">Split Bill Calculator</h3>
            <p className="text-black/80 mb-4 sm:mb-6 text-center font-medium text-sm sm:text-base">
              Calculate how much each person should pay when splitting a bill, including tips.
            </p>
            
            <div className="page-container">
              <div className="input-container space-y-3 sm:space-y-4">
                <div>
                  <label htmlFor='bill' className="block text-black font-black mb-2 tracking-wide uppercase text-xs sm:text-sm">Bill Subtotal:</label>
                  <input
                    type="number"
                    value={billSubtotal}
                    id='bill'
                    onChange={(e) => setBillSubtotal(e.target.value)}
                    className="form-input block w-full bg-white/20 backdrop-blur-sm border-2 border-black/20 text-black rounded-lg sm:rounded-xl p-3 focus:ring-2 focus:ring-black/30 focus:border-black/40 placeholder-black/60 font-bold text-sm sm:text-base"
                    placeholder="Enter Bill Subtotal"
                    required
                  />
                </div>
                <div>
                  <label htmlFor='perce' className="block text-black font-black mb-2 tracking-wide uppercase text-xs sm:text-sm">Tip (%):</label>
                  <input
                    type="number"
                    value={tipPercentage}
                    id='perce'
                    onChange={(e) => setTipPercentage(e.target.value)}
                    className="form-input block w-full bg-white/20 backdrop-blur-sm border-2 border-black/20 text-black rounded-lg sm:rounded-xl p-3 focus:ring-2 focus:ring-black/30 focus:border-black/40 placeholder-black/60 font-bold text-sm sm:text-base"
                    placeholder="Enter Tip Percentage"
                    required
                  />
                </div>
                <div>
                  <label htmlFor='numb' className="block text-black font-black mb-2 tracking-wide uppercase text-xs sm:text-sm">No. of Persons:</label>
                  <input
                    type="number"
                    value={numberOfPersons}
                    id='numb'
                    onChange={(e) => setNumberOfPersons(e.target.value)}
                    className="form-input block w-full bg-white/20 backdrop-blur-sm border-2 border-black/20 text-black rounded-lg sm:rounded-xl p-3 focus:ring-2 focus:ring-black/30 focus:border-black/40 placeholder-black/60 font-bold text-sm sm:text-base"
                    placeholder="Enter No. of Persons"
                    required
                  />
                </div>
              </div>

              <div className="btn-calculate flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mt-4 sm:mt-6">
                <button
                  onClick={calculateBill}
                  className="bg-black text-white font-black py-3 px-6 rounded-lg sm:rounded-xl hover:bg-gray-800 transition-colors duration-200 tracking-wide uppercase text-sm sm:text-base w-full sm:w-auto"
                >
                  Calculate →
                </button>
                <button
                  onClick={clearFields}
                  className="bg-transparent border-2 border-black text-black font-black py-3 px-6 rounded-lg sm:rounded-xl hover:bg-black hover:text-white transition-colors duration-200 tracking-wide uppercase text-sm sm:text-base w-full sm:w-auto"
                >
                  Clear
                </button>
              </div>

              <div 
                className="result-container bg-black/10 backdrop-blur-sm border-2 border-black/20 p-4 sm:p-6 mt-4 sm:mt-6 rounded-lg sm:rounded-xl relative" 
                style={{ minHeight: '160px', maxHeight: '200px' }}
              >
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-black border-t-transparent"></div>
                  </div>
                ) : (
                  <div className="text-black font-bold">
                    <div className="mb-3 sm:mb-4 text-lg sm:text-xl font-black tracking-wide uppercase">Result:</div>
                    {error && <div className="text-red-600 mb-3 sm:mb-4 font-black text-base sm:text-lg">{error}</div>}
                    <div className="mb-2 text-base sm:text-lg flex flex-col sm:flex-row sm:items-center">
                      <span className="font-black uppercase tracking-wide">Total Bill:</span> 
                      <span className="text-xl sm:text-2xl font-black sm:ml-2">{totalBill}</span>
                    </div>
                    <div className="text-base sm:text-lg flex flex-col sm:flex-row sm:items-center">
                      <span className="font-black uppercase tracking-wide">Bill Per Person:</span> 
                      <span className="text-xl sm:text-2xl font-black sm:ml-2">{billPerPerson}</span>
                    </div>
                  </div>
                )}
                
                {/* Background Pattern */}
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none rounded-lg sm:rounded-xl opacity-5">
                  <div 
                    className="w-full h-full"
                    style={{
                      backgroundImage: 'linear-gradient(45deg, currentColor 25%, transparent 25%), linear-gradient(-45deg, currentColor 25%, transparent 25%)',
                      backgroundSize: '8px 8px sm:10px sm:10px',
                      backgroundPosition: '0 0, 0 4px sm:0 5px'
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6 opacity-20">
              <div className="w-8 h-8 sm:w-12 sm:h-12 border-2 sm:border-4 border-black rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillSplitCalculator;